import { useState } from "react";
import { ActionList, ActionMenu, Dialog, Text } from "@primer/react";
import { LinkIcon, PlusIcon } from "@primer/octicons-react";
import { Alert } from "../Alert/Alert.jsx";
import { Button } from "../Button/Button.jsx";
import { SharedProfileStatus } from "../SharedProfile/SharedProfile.jsx";
import { createProfileShare } from "../../domain/profileShare.js";
import { saveSharedProfile } from "../../lib/sharedProfileApi.js";
import { sharedProfileLink } from "../../lib/sharedProfileLink.js";
import { createShareToken } from "../../lib/shareToken.js";
import {
  forgetSharedProfile, rememberSharedProfile, rememberSharedProfileLocalRevision,
  rememberSharedProfileSavedAt, sharedProfileToken
} from "../../storage/profileSharingPreferences.js";
import "./ProfileSyncDialog.css";

const allProfileSections = { configuration: true, allocation: true, browserViews: true };

export function ProfileSyncDialog(props) {
  if (!props.profile) return null;
  return <ProfileSyncDialogContent {...props} />;
}

function ProfileSyncDialogContent({ fetchRequest, onClose, onConnectionChange, onOpenSharedLink, profile, returnFocusRef }) {
  const [token, setToken] = useState(() => sharedProfileToken(profile.id));
  const [status, setStatus] = useState(token ? { tone: "success", text: "Server Sync is enabled." } : null);
  const [working, setWorking] = useState(null);
  const [confirming, setConfirming] = useState(null);

  async function createConnection(regenerating) {
    setWorking(regenerating ? "Creating new link…" : "Enabling Server Sync…");
    setStatus(null);
    try {
      const nextToken = await createShareToken();
      const saved = await saveSharedProfile(fetchRequest, nextToken, profileSnapshot(profile));
      rememberSharedProfile(profile.id, nextToken);
      rememberSharedProfileSavedAt(profile.id, saved.updatedAt);
      rememberSharedProfileLocalRevision(profile.id, profile.updatedAt);
      setToken(nextToken);
      setConfirming(null);
      setStatus({ tone: "success", text: regenerating ? "New sync link created." : "Server Sync enabled." });
      onConnectionChange();
    } catch (error) {
      setStatus({ tone: "danger", text: error.message });
    } finally {
      setWorking(null);
    }
  }

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(sharedProfileLink(token, "sync"));
      setStatus({ tone: "success", text: "Sync link copied." });
    } catch {
      setStatus({ tone: "danger", text: "The sync link could not be copied." });
    }
  }

  function disconnect() {
    forgetSharedProfile(profile.id);
    setToken(null);
    setConfirming(null);
    setStatus({ tone: "success", text: "Server Sync disconnected." });
    onConnectionChange();
  }

  const close = () => { if (!working) onClose(); };
  if (confirming) return <SyncConfirmationDialog action={confirming} onBack={() => setConfirming(null)} onClose={close} onConfirm={confirming === "regenerate" ? () => createConnection(true) : disconnect} returnFocusRef={returnFocusRef} status={status} working={working} />;

  return <Dialog className="profile-sync-dialog" title={`Server Sync for ${profile.name}`} onClose={close} returnFocusRef={returnFocusRef}>
    <div className="profile-sync-dialog-body">
      <Text size="medium" weight="normal">{token
        ? "Server Sync is enabled. Copy the link to connect another device to the same server copy."
        : "Create a new sync link, or connect this profile using a link from another device."}</Text>
    </div>
    <Dialog.Footer className="profile-sync-footer">
      <SyncFooterContent status={status} working={working}>
        {token ? <><ActionMenu><ActionMenu.Button disabled={Boolean(working)}>Manage</ActionMenu.Button><ActionMenu.Overlay><ActionList>
          <ActionList.Item onSelect={() => setConfirming("regenerate")}>Regenerate link</ActionList.Item>
          <ActionList.Divider />
          <ActionList.Item variant="danger" onSelect={() => setConfirming("disconnect")}>Disconnect</ActionList.Item>
        </ActionList></ActionMenu.Overlay></ActionMenu><Button onClick={copyLink} disabled={Boolean(working)}>Copy link</Button></> : <><Button tone="secondary" onClick={close} disabled={Boolean(working)}>Close</Button><ActionMenu><ActionMenu.Button variant="primary" disabled={Boolean(working)}>Set up Server Sync</ActionMenu.Button><ActionMenu.Overlay><ActionList>
          <ActionList.Item onSelect={() => createConnection(false)}><ActionList.LeadingVisual><PlusIcon /></ActionList.LeadingVisual>Create new sync link</ActionList.Item>
          <ActionList.Item onSelect={onOpenSharedLink}><ActionList.LeadingVisual><LinkIcon /></ActionList.LeadingVisual>Connect with existing link…</ActionList.Item>
        </ActionList></ActionMenu.Overlay></ActionMenu></>}
      </SyncFooterContent>
    </Dialog.Footer>
  </Dialog>;
}

function SyncConfirmationDialog({ action, onBack, onClose, onConfirm, returnFocusRef, status, working }) {
  const regenerating = action === "regenerate";
  return <Dialog
    className="profile-sync-dialog"
    title={regenerating ? "Regenerate sync link?" : "Disconnect Server Sync?"}
    subtitle={regenerating ? "This device will switch to a new Server Sync link." : "This device will stop using the shared server copy."}
    onClose={onClose}
    returnFocusRef={returnFocusRef}
  >
    <div className="profile-sync-dialog-body"><Alert tone="warning" heading={regenerating ? "Other devices will not follow automatically" : "The server copy will remain temporarily"}>{regenerating ? "Devices using the current link will remain connected to the old server copy until it expires." : "Other devices with the link can continue using it until it expires. This profile and its local data will not be changed."}</Alert></div>
    <Dialog.Footer className="profile-sync-footer">
      <SyncFooterContent status={status?.tone === "danger" ? status : null} working={working}>
        <Button tone="secondary" onClick={onBack} disabled={Boolean(working)}>Back</Button>
        <Button tone="danger" onClick={onConfirm} disabled={Boolean(working)}>{regenerating ? "Regenerate link" : "Disconnect"}</Button>
      </SyncFooterContent>
    </Dialog.Footer>
  </Dialog>;
}

function SyncFooterContent({ children, status, working }) {
  return <>
    <div className="profile-sync-footer-status">{working ? <Text className="muted-text" role="status">{working}</Text> : <SharedProfileStatus status={status} />}</div>
    <div className="profile-sync-footer-actions">{children}</div>
  </>;
}

function profileSnapshot(profile) {
  return createProfileShare(profile, allProfileSections);
}
